import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  LocalShipping as TruckIcon,
} from '@mui/icons-material';
import axios from '../utils/axios';
import TsdPageBar, { TsdCabinetSelect } from '../components/tsd/TsdPageBar';
import TsdScanCapture from '../components/tsd/TsdScanCapture';
import FulfillmentStatusChip from '../components/fulfillment/FulfillmentStatusChip';
import useFulfillmentCabinet from '../hooks/useFulfillmentCabinet';
import {
  fulfillmentApiBase,
  fulfillmentListPage,
  loadingSessionCapabilities,
  withAccountBody,
  withAccountParams,
} from '../utils/fulfillmentApi.mjs';

/** Только open принимает скан; partial/failed — после confirm, продолжать нельзя. */
const RESTORE_SESSION_STATUS = 'open';

function sessionFromResponse(data) {
  if (!data || typeof data !== 'object') return null;
  const nested = data.session && typeof data.session === 'object' ? data.session : null;
  const session = data.code || data.id != null ? data : (nested || data);
  const pallets = Array.isArray(session.pallets) ? session.pallets : [];
  const orders = pallets.reduce((sum, row) => sum + (Number(row?.pallet?.items_count) || 0), 0);
  return {
    ...session,
    pallets,
    stats: {
      pallets: session.stats?.pallets ?? pallets.length,
      orders: session.stats?.orders ?? orders,
      supplies: session.stats?.supplies ?? 0,
    },
  };
}

function errorText(error) {
  const data = error?.response?.data;
  const base = data?.message || error.message || 'Не удалось выполнить операцию';
  const departed = data?.details?.departed_orders;
  if (!Array.isArray(departed) || !departed.length) return base;
  const lines = departed.slice(0, 12).map((row) => {
    const id = row.posting_number ?? row.wb_order_id ?? '—';
    const article = row.article ? ` · ${row.article}` : '';
    const reason = row.reason || 'выбыл';
    return `${id}${article} — ${reason}`;
  });
  const more = departed.length > 12 ? `\n…и ещё ${departed.length - 12}` : '';
  return `${base}\n${lines.join('\n')}${more}`;
}

export default function FulfillmentLoading() {
  const scanLock = useRef(false);
  /** Soft «Закрыть» на ТСД — не подтягивать ту же open-сессию, пока не сменится кабинет/refresh. */
  const dismissRestoreRef = useRef(false);
  const {
    accounts,
    accountsLoading,
    accountsError,
    accountId,
    setAccountId,
    marketplace,
  } = useFulfillmentCabinet();

  const apiBase = fulfillmentApiBase(marketplace);
  const caps = loadingSessionCapabilities(marketplace);
  const isYandex = marketplace === 'yandex';
  const itemsUnitLabel = isYandex ? 'грузомест' : 'шт.';
  const itemsChipLabel = isYandex ? 'Грузомест' : 'Заказов';

  const [session, setSession] = useState(null);
  const [vehicleLabel, setVehicleLabel] = useState('');
  const [busy, setBusy] = useState(false);
  const [restoring, setRestoring] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  // Смена кабинета/МП сбрасывает локальную сессию (разные API).
  useEffect(() => {
    dismissRestoreRef.current = false;
    setSession(null);
    setNotice('');
    setError('');
  }, [accountId, marketplace]);

  // После refresh / входа: подтянуть незакрытую сессию кабинета (самую свежую open).
  useEffect(() => {
    if (accountsLoading) return undefined;
    if (!accountId) {
      setRestoring(false);
      return undefined;
    }
    if (dismissRestoreRef.current) {
      setRestoring(false);
      return undefined;
    }

    let cancelled = false;
    setRestoring(true);

    (async () => {
      try {
        const { data: list } = await axios.get(`${apiBase}/loading-sessions`, {
          silent: true,
          params: withAccountParams(marketplace, accountId, {
            status: RESTORE_SESSION_STATUS,
            page: fulfillmentListPage(marketplace, 0),
            page_size: 1,
            sort_key: 'id',
            sort_dir: 'desc',
          }),
        });
        if (cancelled || dismissRestoreRef.current) return;
        const head = (list?.items || [])[0];
        if (!head?.id) {
          setSession(null);
          return;
        }
        const { data: detail } = await axios.get(`${apiBase}/loading-sessions/${head.id}`, {
          silent: true,
          params: withAccountParams(marketplace, accountId),
        });
        if (cancelled || dismissRestoreRef.current) return;
        const restored = sessionFromResponse(detail);
        if (!restored || restored.status !== RESTORE_SESSION_STATUS) {
          setSession(null);
          return;
        }
        setSession(restored);
      } catch {
        if (!cancelled) setSession(null);
      } finally {
        if (!cancelled) setRestoring(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [accountId, marketplace, accountsLoading, apiBase]);

  const startSession = async () => {
    if (caps.requiresAccount && !accountId) {
      setError('Выберите кабинет');
      return;
    }
    setBusy(true);
    setError('');
    setNotice('');
    try {
      const body = withAccountBody(
        marketplace,
        accountId,
        caps.supportsVehicleLabel
          ? { vehicle_label: vehicleLabel.trim() || undefined }
          : {},
      );
      const { data } = await axios.post(`${apiBase}/loading-sessions`, body);
      const created = sessionFromResponse(data);
      setSession(created);
      setNotice(`Сессия ${created?.code || ''} создана. Сканируйте закрытые палеты.`);
    } catch (requestError) {
      setError(errorText(requestError));
    } finally {
      setBusy(false);
    }
  };

  const refresh = useCallback(async () => {
    if (!session?.id) return;
    const { data } = await axios.get(`${apiBase}/loading-sessions/${session.id}`, {
      params: withAccountParams(marketplace, accountId),
    });
    setSession(sessionFromResponse(data));
  }, [session?.id, apiBase, marketplace, accountId]);

  const scanPallet = async (raw) => {
    const code = String(raw || '').trim();
    if (!code || !session?.id || busy || scanLock.current) return;
    if (session.status !== 'open') {
      setError('Сессия уже не принимает сканы');
      return;
    }
    scanLock.current = true;
    setBusy(true);
    setError('');
    setNotice('');
    try {
      const { data } = await axios.post(
        `${apiBase}/loading-sessions/${session.id}/scan`,
        withAccountBody(marketplace, accountId, { barcode: code, code }),
      );
      let next = sessionFromResponse(data);
      if (!next?.id) {
        const { data: detail } = await axios.get(`${apiBase}/loading-sessions/${session.id}`, {
          params: withAccountParams(marketplace, accountId),
        });
        next = sessionFromResponse(detail);
      }
      if (!next?.id) {
        setNotice(`Палета ${code} добавлена`);
        return;
      }
      setSession(next);
      const last = next.pallets?.[next.pallets.length - 1];
      setNotice(`Палета ${last?.pallet?.code || code} добавлена (${last?.pallet?.items_count || 0} ${itemsUnitLabel})`);
    } catch (requestError) {
      setError(errorText(requestError));
    } finally {
      setBusy(false);
      scanLock.current = false;
    }
  };

  /** Soft leave TSD screen — session stays open for supervisor confirm. */
  const closeLoadingScreen = () => {
    dismissRestoreRef.current = true;
    setSession(null);
    setError('');
    setNotice('Погрузка закрыта на ТСД. Сессия остаётся открытой для подтверждения начальником.');
  };

  /** Destructive: cancel open session, return pallets to «закрыта», then free TSD. */
  const cancelSession = async () => {
    if (!session?.id) return;
    setBusy(true);
    setError('');
    try {
      await axios.post(
        `${apiBase}/loading-sessions/${session.id}/cancel`,
        withAccountBody(marketplace, accountId, {}),
      );
      dismissRestoreRef.current = true;
      setSession(null);
      setNotice('Сессия отменена, палеты возвращены в «закрыта». Можно начать новую погрузку.');
    } catch (requestError) {
      setError(errorText(requestError));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <TsdPageBar
        title="Погрузка"
        extra={(
          <TsdCabinetSelect
            id="loading-account"
            accounts={accounts}
            accountId={accountId}
            disabled={accountsLoading || Boolean(session)}
            onChange={setAccountId}
          />
        )}
      />
      <Stack spacing={2} sx={{ maxWidth: 920, mx: 'auto', pb: 4 }}>
        {(error || accountsError) && (
          <Alert severity="error" onClose={() => setError('')} sx={{ whiteSpace: 'pre-line' }}>
            {error || accountsError}
          </Alert>
        )}
        {notice && <Alert severity="success" icon={<CheckIcon />} onClose={() => setNotice('')}>{notice}</Alert>}

        {!accountsLoading && !accounts.length && (
          <Alert severity="warning">Нет доступных кабинетов</Alert>
        )}

        {!session && restoring && (
          <Paper variant="outlined" sx={{ p: 3, display: 'flex', justifyContent: 'center' }}>
            <CircularProgress size={32} />
          </Paper>
        )}

        {!session && !restoring && (
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Stack spacing={2}>
              {caps.supportsVehicleLabel && (
                <TextField
                  label="Машина / рейс"
                  value={vehicleLabel}
                  onChange={(e) => setVehicleLabel(e.target.value)}
                  fullWidth
                />
              )}
              <Button
                variant="contained"
                size="large"
                startIcon={busy ? <CircularProgress size={22} color="inherit" /> : <TruckIcon />}
                disabled={busy || (caps.requiresAccount && !accountId)}
                onClick={startSession}
                sx={{ minHeight: 56 }}
              >
                Начать погрузку
              </Button>
            </Stack>
          </Paper>
        )}

        {session && (
          <>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }} justifyContent="space-between">
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="titleLarge">{session.code}</Typography>
                  <Stack direction="row" spacing={1} sx={{ mt: 1 }} flexWrap="wrap" useFlexGap>
                    <FulfillmentStatusChip kind="loadingSession" status={session.status} />
                    {session.vehicle_label && <Chip label={session.vehicle_label} variant="outlined" />}
                    <Chip label={`Палет: ${session.stats?.pallets || 0}`} variant="outlined" />
                    <Chip label={`${itemsChipLabel}: ${session.stats?.orders || 0}`} variant="outlined" />
                    {caps.supportsFinalSuppliesList && (
                      <Chip label={`Поставок: ${session.stats?.supplies || 0}`} variant="outlined" />
                    )}
                  </Stack>
                </Box>
                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                  <Button onClick={refresh} disabled={busy} sx={{ minHeight: 48 }}>Обновить</Button>
                  {session.status === 'open' && (
                    <Button color="warning" variant="outlined" onClick={cancelSession} disabled={busy} sx={{ minHeight: 48 }}>
                      Отменить
                    </Button>
                  )}
                  <Button variant="contained" onClick={closeLoadingScreen} disabled={busy} sx={{ minHeight: 48 }}>
                    Закрыть
                  </Button>
                </Stack>
              </Stack>
            </Paper>

            {session.status === 'open' && (
              <Paper variant="outlined" sx={{ p: 1.5 }}>
                <TsdScanCapture
                  disabled={busy}
                  onScan={scanPallet}
                  helperText="Сканируйте закрытую палету"
                  dialogPlaceholder="ШК / QR палеты"
                  manualVariant="button"
                />
              </Paper>
            )}

            <Paper variant="outlined" sx={{ p: 2 }}>
              {!session.pallets?.length && (
                <Typography color="text.secondary">Пока пусто</Typography>
              )}
              <Stack spacing={1}>
                {session.pallets?.map((row) => (
                  <Paper key={row.id} variant="outlined" sx={{ p: 1.5 }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                      <Box>
                        <Typography fontWeight={600}>{row.pallet?.code || `#${row.pallet_id}`}</Typography>
                        <Typography variant="bodySmall" color="text.secondary">
                          {row.pallet?.items_count || 0} {itemsUnitLabel}
                        </Typography>
                        {row.pallet?.shipment_error && (
                          <Typography variant="bodySmall" color="error" sx={{ mt: 0.5 }}>
                            Ошибка отгрузки: {row.pallet.shipment_error}
                          </Typography>
                        )}
                      </Box>
                      <FulfillmentStatusChip
                        kind="pallet"
                        status={row.pallet?.status}
                        shipmentError={row.pallet?.shipment_error}
                      />
                    </Stack>
                  </Paper>
                ))}
              </Stack>
            </Paper>

            {caps.supportsFinalSuppliesList && !!session.supplies?.length && (
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Stack spacing={1}>
                  {session.supplies.map((supply) => (
                    <Stack key={supply.id} direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap">
                      {supply.wb_supply_id ? (
                        <Typography variant="bodyMedium">{supply.wb_supply_id}</Typography>
                      ) : (
                        <Typography variant="bodyMedium" color="text.secondary">
                          Будет создана при подтверждении
                        </Typography>
                      )}
                      <FulfillmentStatusChip kind="sessionLink" status={supply.status} />
                    </Stack>
                  ))}
                </Stack>
              </Paper>
            )}
          </>
        )}
      </Stack>
    </>
  );
}
